import {useEffect} from 'react';

import appConfig from '../../../../config/appConfig';
import appIntroService from '../../../../services/app/appIntroService';
import iccMarketService from '../../../../services/icc/iccMarketService';
import marketConfigService from '../../../../services/marketConfigService';
import appRouterService from '../../../../services/route/appRouterService';
import userBrowserService from '../../../../services/user/userBrowserService';
import userDeviceService from '../../../../services/user/userDeviceService';
import {selectDoesNavigationTargetExist} from '../../../../state/selectors/global';
import {getState} from '../../../../state/store';
import loadableRoutes from '../../../Routes/loadableRoutes';
import browserCompatibilityService from '../../browserCompatibilityService';

const useBrowserVerification = () => {
    let timer;

    useEffect(() => {
        const isMobileBleDisabled = checkIsMobileBleDisabled();

        if (isMobileBleDisabled) return;

        verifyBrowser();
        return () => clearTimeout(timer);
    }, []);

    const verifyBrowser = async () => {
        const shouldRedirectToPwaPage = checkIfRedirectionToPwaPageIsNeeded();

        if (shouldRedirectToPwaPage) {
            appRouterService.forwardToWelcomePwaPage();
            return;
        }

        const isBleSupported = userDeviceService.isBluetoothSupported();
        const downloadButtonData = browserCompatibilityService.getBrowserCompatibleButtonData();
        const isDownloadButtonAvailable = !!downloadButtonData?.link;

        const isIOSTouchDevice = userDeviceService.isIOSTouchDevice();
        const shouldRedirectToNotCompatiblePage = isIOSTouchDevice && !userBrowserService.isBrowserTrusted();

        const timeout = shouldRedirectToNotCompatiblePage ? 2000 : 0;
        const isBleEnabled = appConfig.getConnectivityIsBle();

        if (isBleEnabled) {
            await userDeviceService.checkBluetoothAvailability();
        }

        const timeoutFunction = async () => {
            const isBrowserCompatiblePageEnabled = appConfig.getIsBrowserCompatiblePageEnabled();

            const forwardToNotCompatibleBrowserPage = async () => {
                await loadableRoutes.NotCompatibleBrowserPage.load();
                appRouterService.forwardToNotCompatibleBrowserPage();
            };

            if (shouldRedirectToNotCompatiblePage) {
                await forwardToNotCompatibleBrowserPage();
            } else {
                const isBleNotSupportedWithAvailableConnectApp = !isBleSupported && isDownloadButtonAvailable;
                const forwardToAppIntroPage = async () => {
                    await loadableRoutes.AppIntroPage.load();
                    appRouterService.forwardToAppIntroPage();
                };

                if (isBleSupported && !isBrowserCompatiblePageEnabled) {
                    browserCompatibilityService.forwardAfterBrowserVerificationPage();
                    return;
                }

                if (isBleSupported || isBleNotSupportedWithAvailableConnectApp) {
                    const isIntroPageVisible = appIntroService.getIsIntroPageVisible();

                    if (isIntroPageVisible) {
                        await forwardToAppIntroPage();
                    } else {
                        if (isBleSupported) {
                            await iccMarketService.getConsumerAggregatedSettings();

                            const isTNCEnabled = marketConfigService.isTNCEnabled();

                            if (isTNCEnabled) {
                                appRouterService.forwardToTNCTermsOfUsePage();
                            } else {
                                appRouterService.forwardToMyDevicesPage();
                            }
                        } else {
                            await forwardToNotCompatibleBrowserPage();
                        }
                    }
                } else {
                    await forwardToAppIntroPage();
                }
            }
        };

        timer = setTimeout(() => {
            timeoutFunction();
        }, timeout);
    };
};

const checkIsMobileBleDisabled = () => {
    const isBleEnabled = appConfig.getConnectivityIsBle();
    const isDesktopDevice = userDeviceService.isDesktopDevice();

    if (!isDesktopDevice && !isBleEnabled) {
        appRouterService.forwardToImportantPage();
        return true;
    }
};

export const checkIfRedirectionToPwaPageIsNeeded = () => {
    const doesNavigationTargetExist = selectDoesNavigationTargetExist(getState());

    const isPwaDesktopEnabled = appConfig.getIsPwaDesktopEnabled();
    const isTabletDevice = userDeviceService.isTabletDevice() || userDeviceService.isTabletIOSDevice();
    const isDesktopDevice = userDeviceService.isDesktopDevice();
    const isDesktopEnabled = (isDesktopDevice || isTabletDevice) && isPwaDesktopEnabled;

    const isPwaEnabled = appConfig.getIsPwaEnabled();
    const isIOSSafariOrChrome = userBrowserService.isIOSSafariOrChrome();
    const isBluefyBrowser = userBrowserService.isBluefyBrowser();
    const isMobileEnabled = !isTabletDevice && isPwaEnabled && (isIOSSafariOrChrome || isBluefyBrowser);

    // If doesNavigationTargetExist it means that we came from PWA
    // skip redirection to pwa page in this case
    const shouldRedirectToPwaPage = (isDesktopEnabled || isMobileEnabled) && !doesNavigationTargetExist;

    return shouldRedirectToPwaPage;
};

export default useBrowserVerification;
